import sortBy from 'lodash/sortBy';

import mbToBytes from 'enterprise-casting/src/shared/utils/mbToBytes';
import { ArrayLike, isNotNullish } from './typeUtils';
import { toArray } from './arrays';

const kb = 1024;
const Units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] as const;
type Units = typeof Units[number];

export const parseUnit = (unit: Units): Units | undefined => {
  const index = Units.indexOf(unit);
  if (index === -1) return undefined;
  return Units[index];
};

function getDecimals(value: number) {
  return value > 100 ? 0 : 2;
}

export function convertBytes(value: number, from: Units, to: Units) {
  const fromParsed = parseUnit(from);
  const toParsed = parseUnit(to);
  if (!fromParsed || !toParsed) return undefined;
  return (value * kb ** Units.indexOf(fromParsed)) / kb ** Units.indexOf(toParsed);
}

export function formatToClosest(
  bytes: number,
  from: Units,
  to: ArrayLike<Units> = ['MB', 'GB'],
  roundToTwoDecimalPlaces = false
) {
  let bytesToUse = bytes;
  if (Number.isNaN(bytes) || bytes === undefined) bytesToUse = 0;

  if (bytes < 0) return `-${formatToClosest(-bytes, from, to)}`;

  const fromParsed = parseUnit(from);
  const toParsed = sortBy(
    // casts `to` to an array even if it is a single value
    toArray(to)
      // filter out invalid units
      .map(parseUnit)
      .filter(isNotNullish),
    // sort by unit magnitude
    (u) => Units.indexOf(u)
  );
  if (!fromParsed || !toParsed || !toParsed?.length) return undefined;

  const [value, units] = toParsed
    // convert to each unit
    .map((toUnit) => [convertBytes(bytesToUse, fromParsed, toUnit), toUnit] as const)
    .reduce(([acc, accUnits], [val, valUnits]) => {
      if (val == null) return [acc, accUnits];
      if (acc == null) return [val, valUnits];
      if (acc > kb - 1) return [val, valUnits];
      return [acc, accUnits];
    });
  return `${parseFloat(value.toFixed(roundToTwoDecimalPlaces ? 2 : getDecimals(value)))} ${units}`;
}

export function formatBytes(bytes: number, units = Units[0]) {
  return formatToClosest(bytes, units, ['MB', 'GB']);
}

export function formatTotalUsedBytes(bytesUsed: number, storageLimitBytes: number) {
  const remainingBytes = storageLimitBytes - bytesUsed;
  if (remainingBytes > 0) {
    // If the talent user is close (i.e. less than 1MB) to their storage limit, format the bytes to round to two decimal places
    // to give the user a more accurate representation of their storage usage.
    const formattedBytesUsed =
      remainingBytes < mbToBytes(1)
        ? formatToClosest(bytesUsed, Units[0], ['MB', 'GB'], true)
        : formatToClosest(bytesUsed, Units[0], ['MB', 'GB'], false);
    // If the formatted bytes used string is the same as the formatted storage limit string even when the talent user has some storage left,
    // it can confuse the user. So we need show some difference between the two strings.
    if (formattedBytesUsed === formatToClosest(storageLimitBytes, Units[0], ['MB', 'GB'])) {
      const stringArr = formattedBytesUsed.split(' ');
      const remaining = Number((remainingBytes / storageLimitBytes).toFixed(2));
      // grab the measurable difference (the largest of 0.01 or the remaining storage)
      const measurableDifference = Math.max(remaining, 0.01);
      // subtract the measurable difference from the rounded total
      const value = Number(stringArr[0]) - measurableDifference;
      return `${value} ${stringArr[1]}`;
    }
    return formattedBytesUsed;
  }
  return formatToClosest(bytesUsed, Units[0], ['MB', 'GB'], false);
}
